import { useRef, useState } from 'react';
import toast from 'react-hot-toast';

import ClientFarmSearch from '../../clients/forms/search';
import UserSelect from '../../users/forms/search';
import { IconClose, IconPlus, IconTrash } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { TE_ROLES, TE_ROLE_LABEL } from '../../../core/constants/transfers';

// Inicio de una sesión TE (la finca suele ser distinta a la de la OPU):
//   · finca   (requerida) — buscador combinado cliente/finca
//   · equipo  (opcional)  — usuarios + rol TE (transfer_vet / synchronizer / …)
//   · fecha   (opcional)  — vacía = hoy
// Devuelve { farm, staff, date } al confirmar.

const TODAY = new Date().toLocaleDateString('en-CA');

export default function TransferStartModal({ onConfirm, onCancel }) {
    const cardRef = useModalDialog(onCancel);
    const [farm, setFarm] = useState(null);
    const [staff, setStaff] = useState([]);
    const [staffUser, setStaffUser] = useState(null);
    const [staffRole, setStaffRole] = useState('transfer_vet');
    const [userKey, setUserKey] = useState(0);
    const [date, setDate] = useState('');
    const seq = useRef(0);

    const addStaff = () => {
        if (!staffUser) { toast.error('Elegí un usuario'); return; }
        if (staff.some(s => s.user.id === staffUser.id && s.role === staffRole)) { toast.error('Ese usuario ya tiene ese rol'); return; }
        setStaff(prev => [...prev, { key: `s${++seq.current}`, user: staffUser, role: staffRole }]);
        setStaffUser(null);
        setUserKey(k => k + 1);
    };
    const removeStaff = (key) => setStaff(prev => prev.filter(s => s.key !== key));

    const confirm = () => {
        if (!farm) { toast.error('La finca es obligatoria'); return; }
        onConfirm({ farm, staff, date: date || null });
    };

    return (
        <div className="transfer-modal">
            <div className="transfer-modal__overlay" onClick={onCancel} aria-hidden="true" />
            <div className="transfer-modal__card" role="dialog" aria-modal="true" aria-label="Nueva sesión de transferencia" ref={cardRef}>
                <header className="transfer-modal__head">
                    <h2 className="transfer-modal__title">Nueva sesión de transferencia</h2>
                    <button type="button" className="transfer-modal__close" onClick={onCancel} aria-label="Cerrar"><IconClose /></button>
                </header>

                <div className="transfer-modal__body">
                    <div className="transfer-field">
                        <span className="transfer-field__label">Finca de la transferencia</span>
                        {farm ? (
                            <div className="transfer-chip">
                                <span className="transfer-chip__text">{farm.name}{farm.owner?.name ? ` · ${farm.owner.name}` : ''}</span>
                                <button type="button" className="transfer-chip__clear" onClick={() => setFarm(null)} aria-label="Cambiar finca"><IconClose /></button>
                            </div>
                        ) : (
                            <ClientFarmSearch farmsOnly onSelect={(it) => setFarm({ id: it.id, name: it.name, owner: it.owner || null })} placeholder="Buscar finca o dueño…" autoFocus />
                        )}
                    </div>

                    <div className="transfer-field">
                        <span className="transfer-field__label">Equipo (opcional)</span>
                        <div className="transfer-staff__add">
                            <UserSelect key={userKey} onSelect={setStaffUser} placeholder="Buscar usuario…" />
                            <select value={staffRole} onChange={(e) => setStaffRole(e.target.value)}>
                                {TE_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                            <button type="button" className="transfer-staff__add-btn" onClick={addStaff}><IconPlus /><span>Agregar</span></button>
                        </div>
                        {staff.length > 0 && (
                            <ul className="transfer-staff__list">
                                {staff.map(s => (
                                    <li key={s.key} className="transfer-staff__item">
                                        <span className="transfer-staff__name">{s.user.name}</span>
                                        <span className="transfer-staff__role">{TE_ROLE_LABEL[s.role]}</span>
                                        <button type="button" className="transfer-staff__remove" onClick={() => removeStaff(s.key)} aria-label="Quitar"><IconTrash /></button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="transfer-field">
                        <span className="transfer-field__label">Fecha de la jornada (opcional)</span>
                        <input type="date" className="transfer-field__date" value={date} max={TODAY} onChange={(e) => setDate(e.target.value)} />
                        <span className="transfer-field__hint">Dejala vacía para hoy. Usá una fecha solo si registrás una jornada pasada.</span>
                    </div>
                </div>

                <footer className="transfer-modal__foot">
                    <button type="button" className="transfer-modal__cancel" onClick={onCancel}>Cancelar</button>
                    <button type="button" className="transfer-modal__continue" onClick={confirm}>Continuar</button>
                </footer>
            </div>
        </div>
    );
}
